import { useCallback, useEffect } from 'react';

import {
  InvoiceItemsViewData,
  InvoiceItemsViewFields,
  InvoiceItemsViewGroupByOptions
} from '../../InvoiceItemsView.types';
import {
  ProformaInvoiceFormGroupByMode,
  ProformaInvoiceFormViewMode
} from '../../../../../teams/components/forms/ProformaInvoiceForm/ProformaInvoiceForm.types';
import { Currencies } from '../../../../../../types';

import { useReactHookForm } from '../../../../../common/hooks/useReactHookForm';
import { usePreviousValue } from '../../../../../../common/hooks/usePreviousValue';
import { isNewValueAndSet } from '../../../../../../utils/isNewValueAndSet';

interface ProformaInvoiceFormFieldsOptions {
  defaultCurrency: Currencies;
  defaultViewMode: ProformaInvoiceFormViewMode;
  defaultGroupBy: ProformaInvoiceFormGroupByMode;
  defaultEnableGrouping: boolean;
  groupByOptions: InvoiceItemsViewGroupByOptions;
}

function useInvoiceItemsViewFields({
  defaultCurrency,
  defaultViewMode,
  defaultGroupBy,
  defaultEnableGrouping,
  groupByOptions
}: ProformaInvoiceFormFieldsOptions) {
  const { control, errors, register, setValue, watch } =
    useReactHookForm<InvoiceItemsViewData>({
      defaultValues: {
        viewMode: defaultViewMode,
        groupBy: defaultGroupBy,
        enableGrouping: defaultEnableGrouping,
        currency: defaultCurrency
      }
    });

  const watchViewMode = watch(InvoiceItemsViewFields.VIEW_MODE);
  const watchGroupBy = watch(InvoiceItemsViewFields.GROUP_BY);

  const previousViewMode = usePreviousValue(watchViewMode);

  useEffect(() => {
    if (previousViewMode !== undefined && previousViewMode !== watchViewMode) {
      const isGroupByValid = groupByOptions[watchViewMode]?.some((group) =>
        group.options?.some((opt) => opt.value === watchGroupBy)
      );

      if (!isGroupByValid) {
        setValue(
          InvoiceItemsViewFields.GROUP_BY,
          groupByOptions[watchViewMode]?.[0]?.options?.[0]
            ?.value as ProformaInvoiceFormGroupByMode
        );
      }
    }
  }, [groupByOptions, previousViewMode, setValue, watchGroupBy, watchViewMode]);

  const prevCurrency = usePreviousValue(defaultCurrency);
  const prevDefaultViewMode = usePreviousValue(defaultViewMode);
  const prevDefaultGroupBy = usePreviousValue(defaultGroupBy);
  const prevDefaultEnableGrouping = usePreviousValue(defaultEnableGrouping);

  useEffect(() => {
    if (isNewValueAndSet(prevCurrency, defaultCurrency)) {
      setValue(InvoiceItemsViewFields.CURRENCY, defaultCurrency);
    }
    if (
      isNewValueAndSet(prevDefaultViewMode, defaultViewMode) ||
      isNewValueAndSet(prevDefaultGroupBy, defaultGroupBy) ||
      isNewValueAndSet(prevDefaultEnableGrouping, defaultEnableGrouping)
    ) {
      setValue(InvoiceItemsViewFields.VIEW_MODE, defaultViewMode);
      setValue(InvoiceItemsViewFields.GROUP_BY, defaultGroupBy);
      setValue(InvoiceItemsViewFields.ENABLE_GROUPING, defaultEnableGrouping);
    }
  }, [
    defaultCurrency,
    defaultEnableGrouping,
    defaultGroupBy,
    defaultViewMode,
    prevCurrency,
    prevDefaultEnableGrouping,
    prevDefaultGroupBy,
    prevDefaultViewMode,
    setValue
  ]);

  const handleViewModeChange = useCallback(
    (newViewMode: string) => {
      const newMode = newViewMode as ProformaInvoiceFormViewMode;
      const isGroupByValid = groupByOptions[newMode]?.some((group) =>
        group.options?.some((opt) => opt.value === watchGroupBy)
      );

      if (!isGroupByValid) {
        setValue(
          InvoiceItemsViewFields.GROUP_BY,
          groupByOptions[newMode]?.[0]?.options?.[0]
            ?.value as ProformaInvoiceFormGroupByMode
        );
      }
    },
    [groupByOptions, setValue, watchGroupBy]
  );

  return {
    control,
    errors,
    register,
    setValue,
    handleViewModeChange,
    watchFields: {
      watchViewMode,
      watchGroupBy,
      watchEnableGrouping: watch(InvoiceItemsViewFields.ENABLE_GROUPING)
    },
    registerFields: {
      registerEnabledGrouping: register(InvoiceItemsViewFields.ENABLE_GROUPING)
    }
  };
}

export default useInvoiceItemsViewFields;
